'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Datetime } from '@flipster/react/design-utils/datetime';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { DateFormatter } from '@flipster/utils/datetime';

import { authBunja } from '../../auth/state';

const exchangeName = 'Flipster';

export const ViewMyInfo = () => {
  const { userAtom } = useBunja(authBunja);
  const user = useAtomValue(userAtom);

  return (
    <div
      className={css({
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderColor: 'border.default.level-1',
        pb: 16,
        pt: 8,
        w: '100%',
      })}
    >
      <details
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          alignItems: 'stretch',
          w: '100%',
          '& summary': {
            listStyle: 'none',
            cursor: 'pointer',
            outline: 'none',
            '&::-webkit-details-marker': { display: 'none' },
          },
          '& [data-chevron]': { transition: 'transform 0.2s' },
          '&[open] [data-chevron]': { transform: 'rotate(180deg)' },
        })}
      >
        <summary
          className={hstack({
            gap: 8,
            alignItems: 'center',
            bg: 'transparent',
            border: 'none',
            p: 0,
            px: 4,
            textStyle: 'Text/L/Strong',
            color: 'content.default.level-5',
          })}
        >
          <T id='Travel_rule.r260304.View_my_info' />
          <span data-chevron className={css({ display: 'inline-flex', pointerEvents: 'none' })}>
            <IconButton icon={<ChevronDownIcon />} hierarchy='tertiary' size='small' disabled={false} />
          </span>
        </summary>
        <dl
          className={css({
            display: 'grid',
            gridTemplateColumns: '130px 1fr',
            gap: '8px 12px',
            alignItems: 'center',
            w: '100%',
            px: 4,
            py: 0,
            rounded: 8,
            '& dt': {
              w: 130,
              flexShrink: 0,
              textStyle: 'Text/S/Regular',
              color: 'content.default.level-3',
            },
          })}
        >
          <dt>
            <T id='Common.r250923.Name' />
          </dt>
          <dd className={rowValueClass}>{user?.name ?? '-'}</dd>
          <dt>
            <T id='Travel_rule.r260304.Country_of_residence' />
          </dt>
          <dd className={rowValueClass}>{user?.country ?? '-'}</dd>
          <dt>
            <T id='Travel_rule.r260304.Kyc_completion_date' />
          </dt>
          <dd className={rowValueClass}>
            <Datetime value={user?.kycActivatedAt} format={DateFormatter.DateTimeSec} />
          </dd>
          <dt>
            <T id='Common.r251124.Exchange' />
          </dt>
          <dd className={rowValueClass}>{exchangeName}</dd>
        </dl>
      </details>
    </div>
  );
};

const rowValueClass = css({
  flex: 1,
  minW: 0,
  textAlign: 'right',
  textStyle: 'Text/M/Regular',
  color: 'content.default.level-5',
  wordBreak: 'break-all',
});
